import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography, Input, Space, Col, Row, Select, DatePicker } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

import {
  Form,
  UploadFile,
  FormButton,
  WrapperSelect,
  CustomNotification,
} from 'common/components';
import LinkAssetToQaSpec from '../qa-grid-cell/LinkAssetToQaSpec';

import {
  DATE_FORMAT,
  UPLOAD_MAX_SIZE,
} from 'static/Constants';
import { SUPPORT_TYPES } from 'pages/qa-spec/components/qa-manufacturing-process/constants';

import * as productEndpoints from 'services/product/endpoints';

import { useSelectProductFullDetailData } from 'pages/qa-spec/hooks';
import { DOCUMENT_CERTIFICATE_LIMIT_CHARACTER } from 'pages/qa-spec/constant';

import {
  useUpdateCertificate,
  useValidateCertificate,
} from 'pages/home/ribbon/components/sections/product/components/certificate-actions/hook';
import { useDispatch } from 'react-redux';
import { exportFormDateWithoutTime } from 'utils/formatDate';
import { qaToggleReloadDocumentCertificateTab } from 'pages/qa-spec/controllers/actions';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { useGetCertificateTypes } from 'pages/company-profile/components/tabs/contacts/companyCertificate/queries';

const UploadCertificate = (props) => {
  const [form] = Form.useForm();

  const { id: _id, productId: _productId } = useParams();
  const id = _id || _productId;

  const {
    visible,
    setVisible,
    defaultValue,
    onSuccessCallback,
    disableCertificateType = false,
    qaSpecName,
  } = props;

  const [currentStep, setCurrentStep] = useState(0);
  const [assetId, setAssetId] = useState(null);

  const dispatch = useDispatch();

  const productDetail = useSelectProductFullDetailData({ productId: id });
  const { getCertificatesForProducts } = useUpdateCertificate();

  const onClickBackBtn = () => {
    setCurrentStep(0);
  };

  const onClickMoveToConfig = () => {
    setCurrentStep(1);
  };

  const onFileChange = () => {
    setAssetId(null);
  };

  const handleUploadFile = (response) => {
    if (!response?.data) return;

    const {
      data: { assetId },
    } = response.data;

    if (assetId) {
      setAssetId(assetId);
      setCurrentStep(1);
    }
    return;
  };

  const getProductCertificates = async () => {
    try {
      const { certificateType } = await form.validateFields();
      const params = {
        productIds: [productDetail?.productId],
      };

      getCertificatesForProducts.mutate(params, {
        onSuccess: ({ isSuccess, data }) => {
          if (isSuccess) {
            handleValidate({
              productCertificates: data?.productCertificates,
              certificateType: certificateType,
            });
          }
        },
      });
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const onSubmitConfigForm = async () => {
    const values = await form.getFieldsValue();

    try {
      const params = {
        ...values,
        digitalAssetId: assetId,
        linkType: 'Certificate',
        productItemId: productDetail?.productId,
        certificateExpiryDate: exportFormDateWithoutTime(
          values?.certificateExpiryDate
        ),
      };

      await addSingleCertificate.mutateAsync(params, {
        onSuccess: ({ isSuccess, message }) => {
          if (isSuccess) {
            setVisible(false);
            onSuccessCallback && onSuccessCallback();
            dispatch(qaToggleReloadDocumentCertificateTab());
            CustomNotification.success('Certificate added successfully');
          } else {
            CustomNotification.error(message || 'Failed to add certificate');
          }
        },
      });
    } catch (error) {
      console.error(error);
      CustomNotification.error('Failed to add certificate');
    }
  };

  const { handleValidate } = useValidateCertificate({
    onSubmit: onSubmitConfigForm,
  });

  const { addSingleCertificate } = useUpdateCertificate();

  const renderUploadFile = () => {
    return (
      <>
        <div style={{ height: 300, marginTop: 12 }}>
          <UploadFile
            multiple={false}
            multipleImport={false}
            supportTypes={SUPPORT_TYPES}
            showSubmitBtn
            showList
            manualUpload
            showPreview
            showMultiple={false}
            additionalBodyPayload={{
              assetType: 'Document',
              assetSubType: 'Certificate',
              ownerId: productDetail?.ownerId,
              productId: id,
            }}
            apiUrl={productEndpoints.UPLOAD_PIM_ASSET}
            maxSize={UPLOAD_MAX_SIZE.GENERAL}
            getResponse={handleUploadFile}
            onFileChange={onFileChange}
            forceDisableManudalUpload={assetId}
          />
        </div>
        {assetId ? (
          <Row justify='end' style={{ marginTop: 12 }}>
            <FormButton
              type='default'
              icon={<ArrowRightOutlined />}
              text='Move to next step'
              onClick={onClickMoveToConfig}
            />
          </Row>
        ) : null}
      </>
    );
  };

  const renderConfigForm = () => {
    const { data, isLoading, isError } = useGetCertificateTypes();
    return (
      <Form
        style={{ height: 360 }}
        form={form}
        onFinish={getProductCertificates}
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 14 }}
        initialValues={{
          type: 'Document',
          subtype: 'Certificate',
          ...defaultValue,
        }}
      >
        <Row
          style={{ flexDirection: 'column', height: '100%', paddingTop: 30 }}
        >
          <Col>
            <Form.Item
              name='type'
              label={
                <TraceableCommentContainer
                  qaSpecName={qaSpecName}
                  qaSpecFieldName='Type'
                >
                  <QaSpecHelpContentLabel
                    fieldPath={TOOlTIP_FIELDS.type.fieldPath}
                  >
                    Type
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
            >
              <WrapperSelect disabled>
                <Select.Option value='Document'>Document</Select.Option>
              </WrapperSelect>
            </Form.Item>

            <Form.Item
              name='subtype'
              label={
                <TraceableCommentContainer
                  qaSpecName={qaSpecName}
                  qaSpecFieldName='Sub Type'
                >
                  <QaSpecHelpContentLabel
                    fieldPath={TOOlTIP_FIELDS.certificateSubType.fieldPath}
                  >
                    Sub Type
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
            >
              <WrapperSelect disabled>
                <Select.Option value='Certificate'>Certificate</Select.Option>
              </WrapperSelect>
            </Form.Item>
            <Form.Item
              name='certificateType'
              label={
                <TraceableCommentContainer
                  qaSpecName={qaSpecName}
                  qaSpecFieldName='Certificate Type'
                >
                  <QaSpecHelpContentLabel
                    fieldPath={TOOlTIP_FIELDS.certificateType.fieldPath}
                  >
                    Certificate Type
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
            >
              <WrapperSelect disabled={disableCertificateType}
                showSearch
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                loading={isLoading}
                allowClear
              >
                {data?.map((item) => (
                  <Select.Option key={item} value={item}>
                    {item}
                  </Select.Option>
                ))}
              </WrapperSelect>
            </Form.Item>
            <Form.Item
              label={
                <TraceableCommentContainer
                  qaSpecName={qaSpecName}
                  qaSpecFieldName='Certificate'
                >
                  <QaSpecHelpContentLabel
                    fieldPath={TOOlTIP_FIELDS.certificateName.fieldPath}
                  >
                    Certificate
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              name='certificate'
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={
                <TraceableCommentContainer
                  qaSpecName={qaSpecName}
                  qaSpecFieldName='Certificate Body'
                >
                  <QaSpecHelpContentLabel
                    fieldPath={TOOlTIP_FIELDS.certificateBody.fieldPath}
                  >
                    Certificate Body
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              name='certificateBody'
            >
              <Input
                showCount
                maxLength={DOCUMENT_CERTIFICATE_LIMIT_CHARACTER.certificateBody}
              />
            </Form.Item>
            <Form.Item
              label={
                <TraceableCommentContainer
                  qaSpecName={qaSpecName}
                  qaSpecFieldName='Certificate Expiry Date'
                >
                  <QaSpecHelpContentLabel
                    fieldPath={TOOlTIP_FIELDS.certificateExpiryDate.fieldPath}
                  >
                    Certificate Expiry Date
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              name='certificateExpiryDate'
              rules={[
                {
                  required: true,
                  message: 'Please select certificate expiry date!',
                },
              ]}
            >
              <DatePicker format={DATE_FORMAT.SYSTEM_FORMAT} />
            </Form.Item>
          </Col>
          <Col flex={1}></Col>
          <Col>
            <Space style={{ float: 'right' }}>
              <FormButton
                type='default'
                icon={<ArrowLeftOutlined />}
                text='Back to asset creation'
                onClick={onClickBackBtn}
              />
              <FormButton
                htmlType='submit'
                text='Complete Setup'
                loading={
                  getCertificatesForProducts?.isLoading ||
                  addSingleCertificate?.isLoading
                }
              />
            </Space>
          </Col>
        </Row>
      </Form>
    );
  };

  return (
    <LinkAssetToQaSpec
      modalProps={{
        title: 'Link certificate document',
        visible: visible,
        onCancel: () => setVisible(false),
        width: 460,
        bodyStyle: {
          minHeight: '375px',
        },
      }}
      stepProps={{
        currentStep,
        items: [
          {
            key: 0,
            title: (
              <Typography.Text strong style={{ fontSize: 16 }}>
                Create an asset
              </Typography.Text>
            ),
          },
          {
            key: 1,
            title: (
              <Typography.Text strong style={{ fontSize: 16 }}>
                Link to QA specification
              </Typography.Text>
            ),
          },
        ],
      }}
    >
      <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
        {renderUploadFile()}
      </div>
      <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
        {renderConfigForm()}
      </div>
    </LinkAssetToQaSpec>
  );
};

export default UploadCertificate;
